import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Droplets, Pill, Phone, Stethoscope } from 'lucide-react';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';

/**
 * EmergencySummary - Quick view of critical emergency information
 * 
 * Now reads from SHARED MEDICAL DATA (single source of truth)
 * Changes made in Medical section appear here automatically
 * In DEMO MODE, uses local demo data instead of API
 */

const EmergencySummary = ({ profileId, profileName }) => {
  const { isDemoMode, getMedicalProfile, getAllergies, getMedications, getConditions, getEmergencyContacts } = useDemo();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, [isDemoMode]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      
      // DEMO MODE: Build summary from demo data
      if (isDemoMode) {
        const profile = getMedicalProfile();
        const allergies = getAllergies();
        const medications = getMedications();
        const conditions = getConditions();
        const contacts = getEmergencyContacts();
        
        setSummary({
          blood_type: profile?.blood_type,
          // Map objects to strings (aligned with demoData/MedicalInfoPage field names)
          allergies: allergies?.slice(0, 3).map(a => a.name || a.allergen).filter(Boolean) || [],
          medications: medications?.slice(0, 3).map(m => m.name).filter(Boolean) || [],
          conditions: conditions?.slice(0, 3).map(c => c.condition || c.name).filter(Boolean) || [],
          primary_contact: contacts?.find(c => c.is_primary) || contacts?.[0] || null
        });
        setLoading(false);
        return;
      }
      
      // REAL MODE: Use shared medical data - single source of truth
      const response = await API.get('/shared-medical/emergency-summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch emergency summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-200 rounded w-1/3"></div>
            <div className="h-3 bg-slate-100 rounded w-full"></div>
            <div className="h-3 bg-slate-100 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if summary has any data
  const hasData = summary && (
    summary.blood_type ||
    (summary.allergies && summary.allergies.length > 0) ||
    (summary.medications && summary.medications.length > 0) ||
    summary.primary_doctor ||
    summary.emergency_contact
  );

  return (
    <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-white" data-testid="emergency-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          {profileName} — Emergency Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {!hasData ? (
          <p className="text-sm text-slate-500 py-2">
            No emergency information added yet. Start by adding medical information.
          </p>
        ) : (
          <div className="space-y-3">
            {/* Blood Type */}
            {summary.blood_type && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <Droplets className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Blood Type</div>
                  <div className="font-semibold text-slate-800">{summary.blood_type}</div>
                </div>
              </div>
            )}

            {/* Allergies */}
            {summary.allergies && summary.allergies.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Allergies</div>
                  <div className="font-semibold text-amber-700">
                    {summary.allergies.join(', ') || 'None'}
                  </div>
                </div>
              </div>
            )}

            {/* Medications */}
            {summary.medications && summary.medications.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Pill className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Medications</div>
                  <div className="font-medium text-slate-800">
                    {summary.medications.join(', ') || 'None'}
                  </div>
                </div>
              </div>
            )}

            {/* Doctor */}
            {summary.primary_doctor && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Stethoscope className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Doctor</div>
                  <div className="font-medium text-slate-800">
                    {summary.primary_doctor}
                    {summary.primary_doctor_phone && (
                      <span className="text-slate-500 ml-1">• {summary.primary_doctor_phone}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Emergency Contact */}
            {summary.emergency_contact && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Emergency Contact</div>
                  <div className="font-medium text-slate-800">
                    {summary.emergency_contact}
                    {summary.emergency_contact_relationship && (
                      <span className="text-slate-500 ml-1">({summary.emergency_contact_relationship})</span>
                    )}
                    {summary.emergency_contact_phone && (
                      <span className="text-slate-500 ml-1">• {summary.emergency_contact_phone}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmergencySummary;
